// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Link, { LinkProps } from "@insite/mobius/Link";
import Select from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FlexWrapper, {
    FlexWrapperPresentationProps,
    FlexWrapperProps,
} from "@store/Library/Components/Toolkit/FlexWrapper";
import updateChangeRequestOrder from "@store/Framework/Store/Greif/Pages/RequestOrderChange/Handler/UpdateChangeRequestOrder";
import {
    ChangeRequestLineModel,
    ChangeRequestModel,
} from "@store/Library/Widgets/RequestOrderChange/RequestOrderChangePageContainer";
import { OrderRequestReasonModel } from "@store/Library/Widgets/RequestOrderChange/RequestOrderChangeProductList";
import { orderChangeProps } from "@store/Themes/common/props/orderChange";
import React from "react";
import { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    order: OrderModel;
    orderLine: OrderLineModel;
    didItemShip: boolean;
    changeRequestReasons: OrderRequestReasonModel[];
    currentLine: ChangeRequestLineModel;
    changeRequestOrder: ChangeRequestModel;
}

const mapStateToProps = (state: ApplicationState) => ({
    changeRequestOrder: state.greif.pages.orderChange.changeRequestOrder,
});

const mapDispatchToProps = {
    updateChangeRequestOrder,
};

interface OrderChangeRequestLinesStyles {
    undoRemoveLink?: LinkProps;
    removeLink?: LinkProps;
    changeRequestGridContainer?: GridContainerProps;
    shipmentDetailsWrapper?: InjectableCss;
    changeRequestFlexWrapper?: FlexWrapperProps;
    editableNotesTextArea?: TextFieldPresentationProps;
    qtyTextField?: TextFieldPresentationProps;
    cancelLineActionWrapper?: InjectableCss;
    cancelLineActionFlexWrapper?: FlexWrapperPresentationProps;
    cancelActionFiller?: InjectableCss;
}

const orderChangeRequestLinesStyles: OrderChangeRequestLinesStyles = {
    undoRemoveLink: orderChangeProps.undoRemoveLink,
    removeLink: orderChangeProps.removeLink,
    changeRequestGridContainer: orderChangeProps.changeRequestGridContainer,
    shipmentDetailsWrapper: orderChangeProps.shipmentDetailsWrapper,
    changeRequestFlexWrapper: orderChangeProps.changeRequestFlexWrapper,
    editableNotesTextArea: orderChangeProps.editableNotesTextArea,
    qtyTextField: orderChangeProps.qtyTextField,
    cancelLineActionWrapper: orderChangeProps.lineActionWrapper,
    cancelLineActionFlexWrapper: orderChangeProps.lineActionFlexWrapper,
    cancelActionFiller: orderChangeProps.actionFiller,
};

const styles = orderChangeRequestLinesStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const OrderChangeRequestOrderLineRequest = ({
    orderLine,
    didItemShip,
    changeRequestReasons,
    currentLine,
    changeRequestOrder,
    updateChangeRequestOrder,
}: Props) => {
    const [changedLine, setChangedLine] = useState<ChangeRequestLineModel>(currentLine);
    const [newQty, setNewQty] = useState<string | number>(
        Number(changedLine.newQuantity) || orderLine.qtyOrdered - orderLine.qtyShipped,
    );
    const [requestNotes, setRequestNotes] = useState<string>("");
    const [requestReason, setRequestReason] = useState<string>("");
    const [hasQtyFieldError, setHasQuantityFieldError] = useState<boolean>(false);
    const [qtyErrorMessage, setQtyErrorMessage] = useState<string>("");

    useEffect(() => {
        if (currentLine) {
            setChangedLine(currentLine);
            currentLine?.changeNotes ? setRequestNotes(currentLine.changeNotes) : null;
            currentLine?.changeReason ? setRequestReason(currentLine.changeReason) : null;
            currentLine?.newQuantity ? setNewQty(Number(currentLine.newQuantity)) : null;
        }
    }, [currentLine]);

    useEffect(() => {
        if (changeRequestOrder) {
            let changedOrderLines: ChangeRequestLineModel[] = changeRequestOrder.orderLines;

            if (changeRequestOrder.orderLines) {
                changedOrderLines = changeRequestOrder.orderLines.map(line =>
                    line.orderLineId === changedLine.orderLineId ? changedLine : line,
                );
            }

            const changedOrder = {
                ...changeRequestOrder,
                orderLines: changedOrderLines,
            };
            updateChangeRequestOrder({ changeRequestOrder: changedOrder });
        }
    }, [changedLine]);

    const reasonChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setRequestReason(event.target.value);
        setChangedLine({
            ...changedLine,
            changeReason: event.target.value,
        });
    };

    const notesChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRequestNotes(event.target.value);
    };

    const notesBlurHandler = () => {
        setChangedLine({
            ...changedLine,
            changeNotes: requestNotes,
        });
    };

    const changeQtyHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setNewQty(event.target.value);
    };

    const onBlurQtyHandler = () => {
        if (newQty <= 0) {
            setHasQuantityFieldError(true);
            setQtyErrorMessage(translate("Quantity must be a minimum of 1."));
        } else {
            setHasQuantityFieldError(false);
            setQtyErrorMessage("");
        }

        setChangedLine({
            ...changedLine,
            newQuantity: newQty,
        });
    };

    const cancelItemHandler = () => {
        setChangedLine({
            ...changedLine,
            isCanceled: !changedLine?.isCanceled,
        });
    };

    const cancelItemAction = currentLine?.isCanceled ? (
        <Link {...styles.undoRemoveLink} onClick={cancelItemHandler}>
            {translate("Reactivate item")}
        </Link>
    ) : (
        <Link {...styles.removeLink} onClick={cancelItemHandler}>
            {translate("Cancel item")}
        </Link>
    );

    if (!orderLine.productId) {
        return (
            <GridContainer {...styles.changeRequestGridContainer}>
                <GridItem width={12}>
                    <Typography>{siteMessage("OrderChange_ProductNoLongerAvailable")}</Typography>
                </GridItem>
            </GridContainer>
        );
    }

    return (
        <GridContainer {...styles.changeRequestGridContainer}>
            <GridItem width={12}>
                <StyledWrapper {...styles.shipmentDetailsWrapper}>
                    <FlexWrapper {...styles.changeRequestFlexWrapper}>
                        <Select
                            label={translate("Change Request Reason")}
                            onChange={reasonChangeHandler}
                            value={requestReason}
                        >
                            <option disabled value="">
                                {translate("Select a reason...")}
                            </option>
                            {changeRequestReasons.map(reason => (
                                <option value={reason.fields.reason} key={reason.fields.reason}>
                                    {reason.fields.reason}
                                </option>
                            ))}
                        </Select>
                        <TextField
                            {...styles.editableNotesTextArea}
                            value={requestNotes}
                            type="text"
                            id={`notes-${orderLine.id}`}
                            onChange={notesChangeHandler}
                            onBlur={notesBlurHandler}
                            label={translate("Change Request Note")}
                            placeholder={translate("Change Request Notes")}
                            disabled={didItemShip || orderLine.productId === null}
                            data-test-selector="OrderChangeRequest_notes"
                        />
                        <TextField
                            {...styles.qtyTextField}
                            value={newQty}
                            type="number"
                            onChange={changeQtyHandler}
                            onBlur={onBlurQtyHandler}
                            min={0}
                            max={999999}
                            disabled={didItemShip || orderLine.productId === null || currentLine?.isCanceled}
                            label={translate("New QTY")}
                            data-test-selector="OrderChangeRequest_Qty"
                            error={hasQtyFieldError && qtyErrorMessage}
                        />
                        <FlexWrapper {...styles.cancelLineActionFlexWrapper}>
                            <StyledWrapper {...styles.cancelActionFiller}></StyledWrapper>
                            <StyledWrapper {...styles.cancelLineActionWrapper}>{cancelItemAction}</StyledWrapper>
                        </FlexWrapper>
                    </FlexWrapper>
                </StyledWrapper>
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderChangeRequestOrderLineRequest);
